/**
 * OEE 模块常量定义
 * 
 * @module types/oee/constants
 * @description OEE（设备综合效率）模块的常量和标签映射
 */

import {
  OeeRecordType,
  OeeEquipmentType,
  OeeDataSource,
  OeeDowntimeType,
  OeeDowntimeCategory,
  OeeLossType,
  OeeEquipmentStatus,
  OeePlannedTimeConfigType,
  OeeCycleTimeSource,
  OeeDataQualityGrade,
} from './enums'

/**
 * OEE 记录类型标签
 */
export const OEE_RECORD_TYPE_LABELS: Record<OeeRecordType, string> = {
  [OeeRecordType.REALTIME]: '实时',
  [OeeRecordType.HOURLY]: '小时',
  [OeeRecordType.SHIFT]: '班次',
  [OeeRecordType.DAILY]: '日报',
}

/**
 * 设备类型标签
 */
export const OEE_EQUIPMENT_TYPE_LABELS: Record<OeeEquipmentType, string> = {
  [OeeEquipmentType.IOT]: '自动采集',
  [OeeEquipmentType.NON_IOT]: '人工记录',
}

/**
 * 数据来源标签
 */
export const OEE_DATA_SOURCE_LABELS: Record<OeeDataSource, string> = {
  [OeeDataSource.IOT]: 'IoT采集',
  [OeeDataSource.REPORT]: '报工数据',
  [OeeDataSource.ANDON]: '安灯系统',
  [OeeDataSource.CALC]: '产能推算',
  [OeeDataSource.MANUAL]: '手动录入',
  [OeeDataSource.DEFAULT]: '默认值',
}

/**
 * 数据来源颜色（用于 UI 标识）
 */
export const OEE_DATA_SOURCE_COLORS: Record<OeeDataSource, string> = {
  [OeeDataSource.IOT]: '#52c41a',      // 绿色 - 高可信度
  [OeeDataSource.REPORT]: '#faad14',   // 黄色 - 中可信度
  [OeeDataSource.ANDON]: '#1890ff',    // 蓝色
  [OeeDataSource.CALC]: '#fa8c16',     // 橙色 - 低可信度
  [OeeDataSource.MANUAL]: '#bfbfbf',   // 灰色 - 需核实
  [OeeDataSource.DEFAULT]: '#d9d9d9',  // 浅灰色
}

/**
 * 停机类型标签
 */
export const OEE_DOWNTIME_TYPE_LABELS: Record<OeeDowntimeType, string> = {
  [OeeDowntimeType.UNPLANNED]: '非计划停机',
  [OeeDowntimeType.PLANNED]: '计划内停机',
}

/**
 * 停机原因分类标签
 */
export const OEE_DOWNTIME_CATEGORY_LABELS: Record<OeeDowntimeCategory, string> = {
  [OeeDowntimeCategory.BREAKDOWN]: '设备故障',
  [OeeDowntimeCategory.SETUP]: '换型调整',
  [OeeDowntimeCategory.IDLING]: '空转短停',
  [OeeDowntimeCategory.REDUCED_SPEED]: '速度降低',
  [OeeDowntimeCategory.STARTUP_REJECT]: '启动废品',
  [OeeDowntimeCategory.PRODUCTION_REJECT]: '生产废品',
}

/**
 * 六大损失类型标签
 */
export const OEE_LOSS_TYPE_LABELS: Record<OeeLossType, string> = {
  [OeeLossType.BREAKDOWN]: '设备故障',
  [OeeLossType.SETUP]: '换型调整',
  [OeeLossType.IDLING]: '空转短停',
  [OeeLossType.REDUCED_SPEED]: '速度降低',
  [OeeLossType.STARTUP_REJECT]: '启动废品',
  [OeeLossType.PRODUCTION_REJECT]: '生产废品',
}

/**
 * 六大损失分组
 */
export const OEE_LOSS_GROUPS = {
  /** 可用率损失 */
  availability: [OeeLossType.BREAKDOWN, OeeLossType.SETUP],
  /** 表现性损失 */
  performance: [OeeLossType.IDLING, OeeLossType.REDUCED_SPEED],
  /** 质量损失 */
  quality: [OeeLossType.STARTUP_REJECT, OeeLossType.PRODUCTION_REJECT],
} as const

/**
 * 设备状态标签
 */
export const OEE_EQUIPMENT_STATUS_LABELS: Record<OeeEquipmentStatus, string> = {
  [OeeEquipmentStatus.RUNNING]: '运行中',
  [OeeEquipmentStatus.IDLE]: '空闲',
  [OeeEquipmentStatus.ALARM]: '报警',
  [OeeEquipmentStatus.OFFLINE]: '离线',
}

/**
 * 设备状态颜色
 */
export const OEE_EQUIPMENT_STATUS_COLORS: Record<OeeEquipmentStatus, string> = {
  [OeeEquipmentStatus.RUNNING]: '#52c41a',  // 绿色
  [OeeEquipmentStatus.IDLE]: '#faad14',     // 黄色
  [OeeEquipmentStatus.ALARM]: '#f5222d',    // 红色
  [OeeEquipmentStatus.OFFLINE]: '#bfbfbf',  // 灰色
}

/**
 * 计划时间配置类型标签
 */
export const OEE_PLANNED_TIME_CONFIG_TYPE_LABELS: Record<OeePlannedTimeConfigType, string> = {
  [OeePlannedTimeConfigType.GLOBAL]: '全局配置',
  [OeePlannedTimeConfigType.WORKSHOP]: '车间配置',
  [OeePlannedTimeConfigType.LINE]: '产线配置',
  [OeePlannedTimeConfigType.EQUIPMENT]: '设备配置',
}

/**
 * 理想周期时间来源标签
 */
export const OEE_CYCLE_TIME_SOURCE_LABELS: Record<OeeCycleTimeSource, string> = {
  [OeeCycleTimeSource.PROCESS_ROUTE]: '工艺参数',
  [OeeCycleTimeSource.MANUAL]: '手动配置',
  [OeeCycleTimeSource.AUTO_LEARN]: '自动学习',
}

/**
 * 数据质量等级标签
 */
export const OEE_DATA_QUALITY_GRADE_LABELS: Record<OeeDataQualityGrade, string> = {
  [OeeDataQualityGrade.A]: 'A级 (优秀)',
  [OeeDataQualityGrade.B]: 'B级 (良好)',
  [OeeDataQualityGrade.C]: 'C级 (一般)',
  [OeeDataQualityGrade.D]: 'D级 (较差)',
}

/**
 * 数据质量等级颜色
 */
export const OEE_DATA_QUALITY_GRADE_COLORS: Record<OeeDataQualityGrade, string> = {
  [OeeDataQualityGrade.A]: '#52c41a',  // 绿色
  [OeeDataQualityGrade.B]: '#1890ff',  // 蓝色
  [OeeDataQualityGrade.C]: '#fa8c16',  // 橙色
  [OeeDataQualityGrade.D]: '#f5222d',  // 红色
}

/**
 * 默认计划时间（分钟）
 */
export const OEE_DEFAULT_PLANNED_MINUTES = 480

/**
 * 默认休息时间（分钟）
 */
export const OEE_DEFAULT_BREAK_MINUTES = 60

/**
 * OEE 目标值（建议）
 */
export const OEE_TARGET_VALUES = {
  /** 世界级水平 */
  worldClass: 0.85,
  /** 良好水平 */
  good: 0.70,
  /** 及格水平 */
  acceptable: 0.60,
} as const
